import type { ComponentProps, ReactNode } from "react";
import {
  CanvasToolbarLayoutProvider,
  useCanvasToolbarLayout,
} from "../context/CanvasToolbarLayoutContext";
import { CanvasToolbar } from "./CanvasToolbar";
import { CanvasToolbar as LegacyCanvasToolbar } from "./toolbar/legacy/CanvasToolbar";
import { CanvasToolbarLayoutProvider as LegacyCanvasToolbarLayoutProvider } from "./toolbar/legacy/CanvasToolbarLayoutContext";

type CanvasToolbarProps = ComponentProps<typeof CanvasToolbar>;

interface CanvasPrimaryToolbarMountProps extends CanvasToolbarProps {
  /** The `canvas-toolbar-redraw` flag (D30): the redrawn bar, or main's. */
  redraw: boolean;
  /** The band left clear of the floating chat (0 until measured). */
  controlsMaxWidth: number;
  onHoverChange: (hovering: boolean) => void;
  /** Main's pen kit disarmed the pen on close; the redrawn tray leaves it
   * armed, so the page supplies the disarm separately. */
  onCancelDrawing: () => void;
  /** The hidden file inputs, which keep their refs on the page. */
  children?: ReactNode;
}

/**
 * The primary toolbar's mount — one of two assemblies by flag. Retiring the
 * flag is deleting `toolbar/legacy/` and the `redraw` branch here.
 */
export function CanvasPrimaryToolbarMount({
  redraw,
  controlsMaxWidth,
  onHoverChange,
  onCancelDrawing,
  children,
  ...toolbar
}: CanvasPrimaryToolbarMountProps) {
  const Provider = redraw
    ? CanvasToolbarLayoutProvider
    : LegacyCanvasToolbarLayoutProvider;
  let bar: ReactNode;
  if (redraw) {
    bar = <CanvasToolbar {...toolbar} />;
  } else {
    const {
      onNewChat,
      onNewChatSelect,
      canComment,
      chatOpen,
      isPlacingWidget,
      pendingPlacement,
      onWidgetSelect,
      ...legacy
    } = toolbar;
    void onNewChat;
    void onNewChatSelect;
    void canComment;
    void chatOpen;
    void isPlacingWidget;
    void pendingPlacement;
    void onWidgetSelect;
    bar = (
      <LegacyCanvasToolbar
        {...legacy}
        onCloseDrawMenu={() => {
          legacy.onCloseDrawMenu();
          onCancelDrawing();
        }}
      />
    );
  }
  return (
    <Provider>
      {/* The toolbar's band: the space actually free of floating chrome,
          running from the rail's inner edge to the chat's. It carries its
          own left AND width rather than inheriting the parent's — the parent
          is already narrowed by the same clearances, so relying on it would
          subtract the rail twice and centre the toolbar half a rail too far
          left. The parent cannot simply be shifted instead: the node context
          menu and the quick composer are its siblings and position
          themselves with viewport coordinates. */}
      {redraw ? (
        <RedrawBand onHoverChange={onHoverChange}>
          {bar}
          {children}
        </RedrawBand>
      ) : (
        <div
          className="pointer-events-none absolute inset-y-0 left-0"
          style={{
            width: controlsMaxWidth > 0 ? controlsMaxWidth : undefined,
            right: controlsMaxWidth > 0 ? undefined : 0,
          }}
        >
          <div
            data-canvas-control
            className="pointer-events-auto"
            onMouseEnter={() => onHoverChange(true)}
            onMouseLeave={() => onHoverChange(false)}
          >
            {bar}
            {children}
          </div>
        </div>
      )}
    </Provider>
  );
}

/**
 * The redrawn bar's band: the canvas left clear of the chat's painted edge
 * and the chat-side air (D55), from the layout context. It is the tray's
 * bounds: a popped panel is clamped to it, and a docked one measures its
 * headroom and its horizontal clamp against it.
 */
function RedrawBand({
  onHoverChange,
  children,
}: {
  onHoverChange: (hovering: boolean) => void;
  children: ReactNode;
}) {
  const { bandWidth } = useCanvasToolbarLayout();
  return (
    <div
      className="pointer-events-none absolute inset-y-0 left-0"
      data-tray-bounds=""
      style={{
        width: bandWidth > 0 ? bandWidth : undefined,
        right: bandWidth > 0 ? undefined : 0,
      }}
    >
      <div
        data-canvas-control
        className="pointer-events-auto"
        onMouseEnter={() => onHoverChange(true)}
        onMouseLeave={() => onHoverChange(false)}
      >
        {children}
      </div>
    </div>
  );
}
